import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import { formatCurrency } from '../../../utils/currency';

const ExpenseGroupedTable = ({ 
  groupedExpenses, 
  selectedExpenses, 
  onSelectExpense, 
  onSelectAll, 
  onEdit, 
  onDelete,
  onApprove,
  onReject,
  onSort,
  sortConfig 
}) => {
  const [expandedReceipt, setExpandedReceipt] = useState(null);
  const [collapsedCategories, setCollapsedCategories] = useState(new Set());

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-warning/10 text-warning border-warning/20',
      approved: 'bg-success/10 text-success border-success/20',
      rejected: 'bg-error/10 text-error border-error/20',
      reimbursed: 'bg-primary/10 text-primary border-primary/20'
    };
    return colors?.[status] || 'bg-muted text-muted-foreground border-border';
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: 'Clock',
      approved: 'CheckCircle',
      rejected: 'XCircle',
      reimbursed: 'DollarSign'
    };
    return icons?.[status] || 'Circle';
  };

  const formatDate = (date) => {
    return new Date(date)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const handleSort = (field) => {
    onSort(field);
  };

  const getSortIcon = (field) => {
    if (sortConfig?.field !== field) return 'ChevronsUpDown';
    return sortConfig?.direction === 'asc' ? 'ChevronUp' : 'ChevronDown';
  };

  const toggleCategory = (category) => {
    const newCollapsed = new Set(collapsedCategories);
    if (newCollapsed.has(category)) {
      newCollapsed.delete(category);
    } else {
      newCollapsed.add(category);
    }
    setCollapsedCategories(newCollapsed);
  };

  const getCategoryTotal = (expenses) => {
    return expenses.reduce((sum, expense) => sum + expense.amount, 0);
  };

  const allExpenses = Object.values(groupedExpenses).flat();

  return (
    <>
      {/* Desktop Table View */}
      <div className="hidden lg:block bg-card rounded-lg shadow-sm border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50 border-b border-border">
              <tr>
                <th className="px-4 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectedExpenses?.length === allExpenses?.length && allExpenses?.length > 0}
                    onChange={onSelectAll}
                    className="w-4 h-4 rounded border-border text-primary focus:ring-2 focus:ring-primary/20"
                  />
                </th>
                <th className="px-4 py-3 text-left">
                  <button
                    onClick={() => handleSort('date')}
                    className="flex items-center gap-2 font-body font-medium text-sm text-foreground hover:text-primary transition-smooth"
                  >
                    <span>Date</span>
                    <Icon name={getSortIcon('date')} size={16} />
                  </button>
                </th>
                <th className="px-4 py-3 text-left">
                  <button
                    onClick={() => handleSort('vendor')}
                    className="flex items-center gap-2 font-body font-medium text-sm text-foreground hover:text-primary transition-smooth"
                  >
                    <span>Vendor</span>
                    <Icon name={getSortIcon('vendor')} size={16} />
                  </button>
                </th>
                <th className="px-4 py-3 text-left">
                  <button
                    onClick={() => handleSort('amount')}
                    className="flex items-center gap-2 font-body font-medium text-sm text-foreground hover:text-primary transition-smooth"
                  >
                    <span>Amount</span>
                    <Icon name={getSortIcon('amount')} size={16} />
                  </button>
                </th>
                <th className="px-4 py-3 text-left">
                  <span className="font-body font-medium text-sm text-foreground">Payment</span>
                </th>
                <th className="px-4 py-3 text-left">
                  <span className="font-body font-medium text-sm text-foreground">Receipt</span>
                </th>
                <th className="px-4 py-3 text-right">
                  <span className="font-body font-medium text-sm text-foreground">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(groupedExpenses).map(([category, expenses]) => (
                <React.Fragment key={category}>
                  {/* Category Header */}
                  <tr className="bg-muted/30 border-t-2 border-primary/20">
                    <td colSpan="7" className="px-4 py-3">
                      <button
                        onClick={() => toggleCategory(category)}
                        className="flex items-center gap-3 w-full text-left hover:text-primary transition-smooth"
                      >
                        <Icon 
                          name={collapsedCategories.has(category) ? 'ChevronRight' : 'ChevronDown'} 
                          size={16} 
                        />
                        <span className="font-heading font-semibold text-base text-foreground">
                          {category}
                        </span>
                        <span className="caption text-muted-foreground">
                          ({expenses.length} {expenses.length === 1 ? 'expense' : 'expenses'})
                        </span>
                        <span className="ml-auto data-text font-medium text-foreground">
                          {formatCurrency(getCategoryTotal(expenses))}
                        </span>
                      </button>
                    </td>
                  </tr>
                  {/* Category Expenses */}
                  {!collapsedCategories.has(category) && expenses.map((expense) => (
                    <tr key={expense?.id} className="hover:bg-muted/30 transition-smooth border-b border-border/50">
                      <td className="px-4 py-3 pl-8">
                        <input
                          type="checkbox"
                          checked={selectedExpenses?.includes(expense?.id)}
                          onChange={() => onSelectExpense(expense?.id)}
                          className="w-4 h-4 rounded border-border text-primary focus:ring-2 focus:ring-primary/20"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-body text-sm text-foreground whitespace-nowrap">
                          {formatDate(expense?.date)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col">
                          <span className="font-body text-sm text-foreground">{expense?.vendor}</span>
                          {expense?.description && (
                            <span className="caption text-muted-foreground">{expense?.description}</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="data-text font-medium text-foreground whitespace-nowrap">
                            {formatCurrency(expense?.amount)}
                          </span>
                          {expense?.taxDeductible && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-success/10 text-success border border-success/20">
                              <Icon name="Receipt" size={12} />
                              <span className="caption">Tax</span>
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-body text-sm text-muted-foreground">{expense?.paymentMethod}</span>
                      </td>
                      <td className="px-4 py-3">
                        {expense?.receipt ? (
                          <button
                            onClick={() => setExpandedReceipt(expense?.id)}
                            className="flex items-center gap-1 text-primary hover:text-primary/80 transition-smooth"
                          >
                            <Icon name="Paperclip" size={16} />
                            <span className="caption">View</span>
                          </button>
                        ) : (
                          <span className="caption text-muted-foreground">No receipt</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          {expense?.status === 'pending' && (
                            <>
                              <button
                                onClick={() => onApprove(expense)}
                                className="p-1.5 rounded-md hover:bg-success/10 transition-smooth"
                                aria-label="Approve expense"
                                title="Approve expense"
                              >
                                <Icon name="Check" size={16} color="var(--color-success)" />
                              </button>
                              <button
                                onClick={() => onReject(expense)}
                                className="p-1.5 rounded-md hover:bg-error/10 transition-smooth"
                                aria-label="Reject expense"
                                title="Reject expense"
                              >
                                <Icon name="X" size={16} color="var(--color-error)" />
                              </button>
                            </>
                          )}
                          <button
                            onClick={() => onEdit(expense)}
                            className="p-1.5 rounded-md hover:bg-muted transition-smooth"
                            aria-label="Edit expense"
                          >
                            <Icon name="Edit2" size={16} color="var(--color-primary)" />
                          </button>
                          <button
                            onClick={() => onDelete(expense?.id)}
                            className="p-1.5 rounded-md hover:bg-muted transition-smooth"
                            aria-label="Delete expense"
                          >
                            <Icon name="Trash2" size={16} color="var(--color-error)" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile Card View */}
      <div className="lg:hidden space-y-6">
        {Object.entries(groupedExpenses).map(([category, expenses]) => (
          <div key={category} className="bg-card rounded-lg shadow-sm border border-border overflow-hidden">
            {/* Category Header */}
            <button
              onClick={() => toggleCategory(category)}
              className="w-full px-4 py-3 bg-muted/30 border-b border-border flex items-center justify-between hover:bg-muted/50 transition-smooth"
            >
              <div className="flex items-center gap-3">
                <Icon 
                  name={collapsedCategories.has(category) ? 'ChevronRight' : 'ChevronDown'} 
                  size={16} 
                />
                <span className="font-heading font-semibold text-base text-foreground">
                  {category}
                </span>
                <span className="caption text-muted-foreground">
                  ({expenses.length})
                </span>
              </div>
              <span className="data-text font-medium text-foreground">
                {formatCurrency(getCategoryTotal(expenses))}
              </span>
            </button>

            {/* Category Expenses */}
            {!collapsedCategories.has(category) && (
              <div className="divide-y divide-border">
                {expenses.map((expense) => (
                  <div key={expense?.id} className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <input
                          type="checkbox"
                          checked={selectedExpenses?.includes(expense?.id)}
                          onChange={() => onSelectExpense(expense?.id)}
                          className="mt-1 w-4 h-4 rounded border-border text-primary focus:ring-2 focus:ring-primary/20 flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <h3 className="font-body font-medium text-base text-foreground mb-1">{expense?.vendor}</h3>
                          <p className="caption text-muted-foreground">{formatDate(expense?.date)}</p>
                          {expense?.description && (
                            <p className="caption text-muted-foreground mt-1">{expense?.description}</p>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2 mb-3">
                      <div className="flex items-center justify-between">
                        <span className="caption text-muted-foreground">Amount</span>
                        <div className="flex items-center gap-2">
                          <span className="data-text font-medium text-foreground">{formatCurrency(expense?.amount)}</span>
                          {expense?.taxDeductible && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-success/10 text-success border border-success/20">
                              <Icon name="Receipt" size={10} />
                              <span className="caption text-xs">Tax</span>
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="caption text-muted-foreground">Payment</span>
                        <span className="font-body text-sm text-foreground">{expense?.paymentMethod}</span>
                      </div>
                    </div>

                    <div className="flex items-center justify-between pt-3 border-t border-border">
                      {expense?.receipt ? (
                        <button
                          onClick={() => setExpandedReceipt(expense?.id)}
                          className="flex items-center gap-1.5 text-primary hover:text-primary/80 transition-smooth"
                        >
                          <Icon name="Paperclip" size={16} />
                          <span className="caption">View Receipt</span>
                        </button>
                      ) : (
                        <span className="caption text-muted-foreground">No receipt</span>
                      )}
                      <div className="flex items-center gap-2">
                        {expense?.status === 'pending' && (
                          <>
                            <button
                              onClick={() => onApprove(expense)}
                              className="p-2 rounded-md hover:bg-success/10 transition-smooth"
                              aria-label="Approve expense"
                              title="Approve expense"
                            >
                              <Icon name="Check" size={18} color="var(--color-success)" />
                            </button>
                            <button
                              onClick={() => onReject(expense)}
                              className="p-2 rounded-md hover:bg-error/10 transition-smooth"
                              aria-label="Reject expense"
                              title="Reject expense"
                            >
                              <Icon name="X" size={18} color="var(--color-error)" />
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => onEdit(expense)}
                          className="p-2 rounded-md hover:bg-muted transition-smooth"
                          aria-label="Edit expense"
                        >
                          <Icon name="Edit2" size={18} color="var(--color-primary)" />
                        </button>
                        <button
                          onClick={() => onDelete(expense?.id)}
                          className="p-2 rounded-md hover:bg-muted transition-smooth"
                          aria-label="Delete expense"
                        >
                          <Icon name="Trash2" size={18} color="var(--color-error)" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Receipt Modal */}
      {expandedReceipt && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-1030 flex items-center justify-center p-4">
          <div className="bg-card rounded-lg shadow-2xl border border-border max-w-2xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h3 className="font-heading font-semibold text-lg text-foreground">Receipt</h3>
              <button
                onClick={() => setExpandedReceipt(null)}
                className="p-2 rounded-md hover:bg-muted transition-smooth"
                aria-label="Close receipt"
              >
                <Icon name="X" size={20} />
              </button>
            </div>
            <div className="p-4 overflow-y-auto max-h-[calc(90vh-80px)]">
              <Image
                src={allExpenses?.find(e => e?.id === expandedReceipt)?.receipt}
                alt={`Receipt for ${allExpenses?.find(e => e?.id === expandedReceipt)?.vendor} expense showing transaction details and payment proof`}
                className="w-full h-auto rounded-md"
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ExpenseGroupedTable;